import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchNotificationsApi,
    fetchUnreadNotificationsApi,
    markNotificationsAsReadApi,
    markNotificationsAsUnreadApi,
    deleteNotificationsApi
} from '../api/notificationsApi';
import { updateNotifications, updateUnreadeNotifications } from '../redux/actions/actions';

export const useNotifications = (accessToken, contextId) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [localNotifications, setLocalNotifications] = useState([]);

    const reduxNotifications = useSelector(state => state?.notifications?.notifications ?? []);
    const notificationsCount = useSelector(state => state?.notifications?.count ?? 0); // Add this
    const notifications = reduxNotifications.length ? reduxNotifications : localNotifications;

    const transformNotification = (item) => ({
        notificationsId: item.id,
        id: item.notification.id,
        title: item.notification.title,
        description: item.notification.content,
        isRead: item.read,
        readAt: item.read_at,
        notificationType: item?.notification?.notification_type,
        timestamp: new Date(item.notification.created_at).toLocaleString(),
        createdDate: new Date(item.notification.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hour12: true }).replace(/,([^,]*)$/, " |$1"),
        actionUrl: item.notification.action_url,
        requiresAction: item.notification.requires_action,
        historyId: item.notification.history,
        nodeId: item.notification.node,
        pageNumber: item.page_number
    });

    const fetchNotifications = useCallback(async (page, searchTerm,instanceId) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchNotificationsApi(page, searchTerm, instanceId);
            const transformedNotifications = response.results.map(transformNotification);
            dispatch(updateNotifications({
                notifications: transformedNotifications,
                count: response.count,
                next: response.next,
                previous: response.previous,
                page: response.page_number
            }));
             return response;
        } catch (error) {
            const errorMessage = error?.errors?.length ?
                error.errors.map(err => err.detail).join('. ') :
                'Failed to fetch notifications';
            setError(errorMessage);
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [accessToken, dispatch, contextId]);

    const fetchunReadNotifications = useCallback(async (page = 1, searchTerm) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchUnreadNotificationsApi(page, searchTerm);
            const transformedNotifications = response.results.map(transformNotification);
            dispatch(updateUnreadeNotifications({
                notifications: transformedNotifications,
                count: response.count
            }));

            // Return pagination info
            return {
                next: response.next,
                previous: response.previous,
                count: response.count
            };
        } catch (error) {
            const errorMessage = error?.errors?.length ?
                error.errors.map(err => err.detail).join('. ') :
                'Failed to fetch notifications';
            setError(errorMessage);
            console.error('Error fetching notifications:', error);
            return null;
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    const markAsRead = useCallback(async (notificationIds, page = 1) => {
        try {
            setError(null);
            const response = await markNotificationsAsReadApi(notificationIds);

            if (response.updated_notifications) {
                const updatedNotificationsMap = response.updated_notifications.reduce((acc, item) => {
                    acc[item.notification.id] = transformNotification(item);
                    return acc;
                }, {});

                const updatedNotifications = notifications.map(notification =>
                    updatedNotificationsMap[notification.id] || notification
                );
                dispatch(updateNotifications({
                    notifications: updatedNotifications,
                }));

                setLocalNotifications(updatedNotifications);
                try {
                    await fetchunReadNotifications(page);
                } catch (pageError) {
                    await fetchunReadNotifications(page > 1 ? page - 1 : 1);
                }
            }
        } catch (error) {
            const errorMessage = error?.errors?.length ?
                error.errors.map(err => err.detail).join('. ') :
                'Failed to mark notifications as read';
            setError(errorMessage);
            console.error('Error marking notifications as read:', error);
        }
    }, [accessToken, notifications, , dispatch]);

    const markAsUnread = useCallback(async (notificationIds) => {
        try {
            setError(null);
            const response = await markNotificationsAsUnreadApi(notificationIds);

            if (response.updated_notifications) {
                const updatedNotificationsMap = response.updated_notifications.reduce((acc, item) => {
                    acc[item.notification.id] = transformNotification(item);
                    return acc;
                }, {});

                const updatedNotifications = notifications.map(notification =>
                    updatedNotificationsMap[notification.id] || notification
                )

                dispatch(updateNotifications({
                    notifications: updatedNotifications,
                }));
                setLocalNotifications(updatedNotifications);

                await fetchunReadNotifications();
            }
        } catch (error) {
            const errorMessage = error?.errors?.length ?
                error.errors.map(err => err.detail).join('. ') :
                'Failed to mark notifications as unread';
            setError(errorMessage);
            console.error('Error marking notifications as unread:', error);
        }
    }, [accessToken, notifications, dispatch]);

    const deleteNotifications = useCallback(async (notificationIds, currentPage, searchTerm) => {
        try {
            setError(null);
            await deleteNotificationsApi(notificationIds);

            const updatedNotifications = notifications.filter(
                notification => !notificationIds.includes(notification.id)
            );

            dispatch(updateNotifications({
                notifications: updatedNotifications,
            }));
            setLocalNotifications(updatedNotifications);

            await fetchunReadNotifications();

            const remainingCount = notificationsCount - notificationIds.length;
            const itemsPerPage = 10;
            const maxValidPage = Math.ceil(remainingCount / itemsPerPage);

            // If current page is now invalid, go to the last valid page
            const pageToFetch = currentPage > maxValidPage ? Math.max(1, maxValidPage) : currentPage;

            await fetchNotifications(pageToFetch, searchTerm);

            return pageToFetch;
        } catch (error) {
            const errorMessage = error?.errors?.length ?
                error.errors.map(err => err.detail).join('. ') :
                'Failed to delete notifications';
            setError(errorMessage);
            console.error('Error deleting notifications:', error);
        }
    }, [accessToken, notifications, dispatch]);

    return {
        notifications,
        loading,
        error,
        markAsRead,
        markAsUnread,
        deleteNotifications,
        refreshNotifications: fetchNotifications,
        refreshUnreadNotifications: fetchunReadNotifications,
        transformNotification
    };
};